import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ContratoService } from 'administrativo-lib';
import { Contrato, ContratoItem, FuncaoService, GlobalService, Login, Prazo, Proponente, TipoContratacao, TipoContrato } from 'eddydata-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as toastr from 'toastr';
import { PrazoService } from '../../prazo/service/prazo.service';
import { TipoContratacaoService } from '../../tipo-contratacao/service/tipo-contratacao.service';

declare var $: any;

@Component({
  selector: 'lib-contrato-licitacao-dlg',
  templateUrl: './contrato-licitacao-dlg.component.html'
})
export class ContratoLicitacaoDlgComponent implements OnInit, OnChanges {

  @Input() public login: Login;
  @Input() public proponente: Proponente;
  @Output() callback: EventEmitter<Contrato> = new EventEmitter();

  public tipos: { id: 'CONTRATO' | 'REGISTRO_PRECO', nome: string }[];
  public listaTipos: { id: TipoContrato, nome: string }[];
  public listaContratacao: TipoContratacao[];
  public listaPrazos: Prazo[];

  public entidade: Contrato;
  public entidadeForm: FormGroup;
  protected router: Router;
  protected activatedRoute: ActivatedRoute;
  protected fb: FormBuilder;
  protected unsubscribe: Subject<void> = new Subject();

  constructor(
    protected injector: Injector,
    public funcaoService: FuncaoService,
    protected globalService: GlobalService,
    protected contratacaoService: TipoContratacaoService,
    protected prazoService: PrazoService,
    protected contratoService: ContratoService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes['proponente'] && this.proponente?.licitacao) {
        this.entidade = new Contrato();

        this.entidade.ano = new Date().getFullYear();
        this.entidade.enviar_email = true;
        this.entidade.tipo = 'CONTRATO';
        this.entidade.tipo_contrato = 'CONCESSAO';
        this.entidade.valor_caucao = 0;
        this.entidade.valor_garantia = 0;
        this.entidade.data_contabilizacao = new Date();

        this.entidade.tipo_contratacao = this.proponente.licitacao.tipo_contratacao;
        this.entidade.data_assinatura = this.proponente.licitacao.data_assinatura;
        this.entidade.data_inicio = this.proponente.licitacao.data_inicio;
        this.entidade.data_termino = this.proponente.licitacao.data_termino;
        this.entidade.favorecido = this.proponente.favorecido;
        this.entidade.licitacao = this.proponente.licitacao;
        this.entidade.objeto = this.proponente.licitacao.objeto;
        this.entidade.orgao = this.login.orgao;
        this.entidade.processo = this.proponente.licitacao.processo;

        let total = 0;
        this.entidade.itens = [];
        this.proponente.propostas.forEach(p => {
          const item = new ContratoItem();

          item.produto_unidade = p.memorial.produto_unidade;
          item.descricao = p.memorial.descricao;
          item.unidade = p.memorial.unidade;
          item.quantidade = p.quantidade_final;
          item.valor_total = +Number(+p.quantidade_final * +p.valor_final).toFixed(2);

          this.entidade.itens.push(item);
          total += item.valor_total;
        });
        this.entidade.valor_contrato = total;

        this.entidadeForm.patchValue(this.entidade);

        this.contratoService.proximoNumero(this.login.exercicio.ano)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe((res) => {
            this.entidadeForm.get('numero').setValue(res);
          });

        setTimeout(() => this.globalService.calendarMascara(), 1000);
      }
    }
  }

  ngOnInit(): void {
    this.router = this.injector.get(Router);
    this.fb = this.injector.get(FormBuilder);
    this.activatedRoute = this.injector.get(ActivatedRoute);
    this.criarCamposForm();

    this.tipos = [{ id: 'CONTRATO', nome: 'Contrato' }, { id: 'REGISTRO_PRECO', nome: 'Registro de Preço' }];
    this.listaTipos = this.globalService.obterTiposContrato();
    this.contratacaoService.filtrar(0, 0, { orgao_id: this.login.orgao.id }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaContratacao = res ? res.content : [];
      }, error => toastr.error(error.message ? error.message : error));
    this.prazoService.filtrar(0, 0, { orgao_id: this.login.orgao.id }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaPrazos = res ? res.content : [];
      }, error => toastr.error(error.message ? error.message : error));
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public submitForm() {
    if (this.entidadeForm.invalid) {
      toastr.error('Há campos obrigatórios não informados!');
      return;
    }

    this.salvar();
  }

  private salvar() {
    const entidade: Contrato = Contrato.converteJson(this.entidadeForm.value);
    this.contratoService.inserir(entidade).pipe(takeUntil(this.unsubscribe))
      .subscribe(res => {
        this.acaoSucesso(res);
        this.criarCamposForm();
      }, error => this.acaoErro(error));
  }

  private acaoSucesso(entidade: Contrato) {
    toastr.success('Registro salvo com sucesso!');
    this.callback.emit(entidade);
    this.fechar();
  }

  private acaoErro(error: any) {
    toastr.options.timeOut = 10000;
    toastr.options.closeButton = true;
    toastr.options.tapToDismiss = false;
    if (error.error && error.error.payload) {
      toastr.error(error.error.payload);
    } else {
      toastr.error('Ocorreu um erro ao processar a sua solicitação');
    }
  }

  private criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      numero: [null, [Validators.required]],
      ano: [null, [Validators.required]],
      data_assinatura: [null, [Validators.required]],
      data_inicio: [null, [Validators.required]],
      data_termino: [null, [Validators.required]],
      processo: [null, [Validators.required]],
      tipo_contrato: [null, [Validators.required]],
      tipo: [null, [Validators.required]],
      objeto: [null, [Validators.required]],
      aditivo: [null],
      valor_contrato: [null, [Validators.required]],
      valor_garantia: [null, [Validators.required]],
      valor_caucao: [null, [Validators.required]],
      enviar_email: [null, [Validators.required]],
      parente: [null],
      licitacao: [null, [Validators.required]],
      orgao: [null, [Validators.required]],
      favorecido: [null, [Validators.required]],
      prazo: [null, [Validators.required]],
      tipo_contratacao: [null, [Validators.required]],
      itens: [null],
    });
  }

  compareFn(c1: any, c2: any): boolean {
    if (c1 && c2) {
      if (c1.id && c2.id) {
        return c1.id === c2.id;
      } else if (c1.chave && c2.chave) {
        return c1.chave == c2.chave;
      } else {
        return c1 === c2;
      }
    } else {
      return false;
    }
  }

  public fechar() {
    this.criarCamposForm();
    $('#dialogContratoLicitacao').modal('hide');
  }
}
